import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const RouterContext = createContext(null);

function normalizePath(inputPath) {
  const raw = typeof inputPath === "string" && inputPath.trim() ? inputPath.trim() : "/";
  if (raw === "/") return "/";
  return raw.endsWith("/") ? raw.slice(0, -1) : raw;
}

export function SimpleRouterProvider({ children }) {
  const [path, setPath] = useState(() =>
    normalizePath(typeof window !== "undefined" ? window.location.pathname : "/")
  );

  useEffect(() => {
    const onPopState = () => {
      setPath(normalizePath(window.location.pathname));
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const navigate = useCallback((to, options = {}) => {
    const target = normalizePath(to);
    const replace = Boolean(options && options.replace);
    const current = normalizePath(window.location.pathname);
    if (current === target) return;

    if (replace) window.history.replaceState(null, "", target);
    else window.history.pushState(null, "", target);
    setPath(target);
  }, []);

  const value = useMemo(
    () => ({
      path,
      navigate,
    }),
    [path, navigate]
  );

  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

export function useRouter() {
  const ctx = useContext(RouterContext);
  if (!ctx) {
    throw new Error("useRouter must be used inside SimpleRouterProvider");
  }
  return ctx;
}

export function RouterLink({ to, replace = false, onClick, children, ...rest }) {
  const { navigate } = useRouter();

  return (
    <a
      href={to}
      onClick={(event) => {
        if (onClick) onClick(event);
        if (event.defaultPrevented) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        if (event.button !== 0) return;
        event.preventDefault();
        navigate(to, { replace });
      }}
      {...rest}
    >
      {children}
    </a>
  );
}

